(() => {
  const parseJson = (el, fallback) => {
    if (!el) return fallback;
    try {
      const raw = (el.textContent || '').trim();
      if (!raw) return fallback;
      let parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        const nested = parsed.trim();
        if (
          (nested.startsWith('{') && nested.endsWith('}')) ||
          (nested.startsWith('[') && nested.endsWith(']'))
        ) {
          parsed = JSON.parse(nested);
        }
      }
      return parsed;
    } catch (_) {
      return fallback;
    }
  };

  const asText = (value) => String(value == null ? '' : value).trim();
  const asArray = (value) => (Array.isArray(value) ? value : []);
  const sek = new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const createEl = (tag, cls, text = '') => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text) el.textContent = text;
    return el;
  };

  const normalizeProducts = (payload, rows) =>
    asArray(rows)
      .filter((row) => row && row.id && asText(row.handle))
      .filter((row) => String(row.id) !== String(payload.productId))
      .map((row) => {
        const image = row.featured_image || row.image || '';
        const priceRaw = Number(row.price || 0);
        return {
          id: row.id,
          handle: row.handle,
          title: asText(row.title || ''),
          url: asText(row.url || `/products/${row.handle}`),
          image: asText(image),
          price: Number.isFinite(priceRaw) ? priceRaw : 0,
        };
      });

  const fetchRecs = async (productId, intent) => {
    try {
      const res = await fetch(
        `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=12&intent=${encodeURIComponent(intent)}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok) return [];
      const body = await res.json();
      return asArray(body && body.products);
    } catch (_) {
      return [];
    }
  };

  const makeCard = (row) => {
    const a = createEl('a', 'bp-discovery-card');
    a.href = row.url;

    const media = createEl('div', 'bp-discovery-card__media');
    if (row.image) {
      const img = document.createElement('img');
      img.src = row.image;
      img.alt = row.title || 'Produkt';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    a.appendChild(media);

    const body = createEl('div', 'bp-discovery-card__body');
    body.appendChild(createEl('p', 'bp-discovery-card__title', row.title || 'Produkt'));
    if (row.price > 0) {
      body.appendChild(createEl('p', 'bp-discovery-card__price', sek.format(row.price / 100)));
    }
    a.appendChild(body);
    return a;
  };

  const mountRail = (root, title, rows) => {
    if (!rows.length) return;
    const rail = createEl('section', 'bp-discovery-rail');
    const head = createEl('div', 'bp-discovery-rail__head');
    head.appendChild(createEl('h4', 'bp-discovery-rail__title', title));

    const nav = createEl('div', 'bp-discovery-rail__nav');
    const prev = createEl('button', 'bp-discovery-rail__btn', '‹');
    const next = createEl('button', 'bp-discovery-rail__btn', '›');
    prev.type = 'button';
    next.type = 'button';
    nav.appendChild(prev);
    nav.appendChild(next);
    head.appendChild(nav);
    rail.appendChild(head);

    const viewport = createEl('div', 'bp-discovery-rail__viewport');
    const list = createEl('div', 'bp-discovery-rail__list');
    rows.forEach((row) => list.appendChild(makeCard(row)));
    viewport.appendChild(list);
    rail.appendChild(viewport);

    const updateNav = () => {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const left = Math.max(0, viewport.scrollLeft);
      prev.disabled = left <= 2;
      next.disabled = left >= max - 2;
    };

    const step = () => Math.max(180, Math.round(viewport.clientWidth * 0.9));
    prev.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: 'smooth' }));
    viewport.addEventListener('scroll', updateNav, { passive: true });
    window.setTimeout(updateNav, 0);

    root.appendChild(rail);
  };

  const renderRoot = async (root) => {
    const payload = parseJson(root.querySelector('[data-bp-discovery-payload]'), null);
    if (!payload || !payload.productId) return;

    const shell = createEl('div', 'bp-discovery__shell');
    const header = createEl('div', 'bp-discovery__header');
    const text = createEl('div', 'bp-discovery__header-copy');
    text.appendChild(createEl('h3', 'bp-discovery__title', 'Hitta rätt produkter snabbare'));
    text.appendChild(
      createEl(
        'p',
        'bp-discovery__subtitle',
        'Se fler i samma stil och komplettera med relevanta produkter.'
      )
    );
    header.appendChild(text);
    shell.appendChild(header);

    const chips = createEl('div', 'bp-discovery__chips');
    if (asText(payload.allVendorUrl)) {
      const all = createEl('a', 'bp-discovery__chip', 'Alla Dansani');
      all.href = payload.allVendorUrl;
      chips.appendChild(all);
    }
    if (asText(payload.seriesTag) && asText(payload.seriesSearchUrl)) {
      const series = createEl('a', 'bp-discovery__chip', 'Samma serie');
      series.href = payload.seriesSearchUrl;
      chips.appendChild(series);
    }
    if (asText(payload.l2Tag) && asText(payload.l2SearchUrl)) {
      const cat = createEl('a', 'bp-discovery__chip', 'Samma kategori');
      cat.href = payload.l2SearchUrl;
      chips.appendChild(cat);
    }
    if (chips.childElementCount) {
      shell.appendChild(chips);
    }

    const [relatedRaw, complementaryRaw] = await Promise.all([
      fetchRecs(payload.productId, 'related'),
      fetchRecs(payload.productId, 'complementary'),
    ]);

    const related = normalizeProducts(payload, relatedRaw).slice(0, 12);
    const complementary = normalizeProducts(payload, complementaryRaw).slice(0, 12);

    if (related.length) {
      mountRail(shell, 'Liknande produkter', related);
    }
    if (complementary.length) {
      mountRail(shell, 'Komplettera med', complementary);
    }

    if (!related.length && !complementary.length) {
      shell.appendChild(
        createEl(
          'p',
          'bp-discovery__empty',
          'Vi har inga automatiska förslag här ännu. Använd snabbvägarna ovan för att hitta rätt produkter.'
        )
      );
    }

    root.innerHTML = '';
    root.appendChild(shell);
  };

  const initRoot = (root) => {
    if (!root || root.dataset.bpDiscoveryReady === '1') return;
    root.dataset.bpDiscoveryReady = '1';
    renderRoot(root);
  };

  const initAll = (scope) => {
    const base = scope && scope.querySelectorAll ? scope : document;
    base.querySelectorAll('[data-bp-discovery]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();
